import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import AdminLogin from './AdminLogin';
import AdminNav from './AdminNav';
import AdminSettings from './AdminSettings';
import AdminDashboard from './AdminDashboard';
import AdminProgramForm from './AdminProgramForm';
import AdminContent from './AdminContent';
import AdminClubs from './AdminClubs';
import AdminDocs from './AdminDocs';
import '../../styles/Admin.css';
import '../../styles/AuthPage.css';

const ADMIN_EMAIL = 'sam@beginthework.com';

function AdminLayout() {
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setAuthChecked(true);
      if (!firebaseUser) {
        setUser(null);
        setIsAdmin(false);
        setAccessDenied(false);
        return;
      }
      if (firebaseUser.email !== ADMIN_EMAIL) {
        setAccessDenied(true);
        signOut(auth);
        return;
      }
      setUser(firebaseUser);
      setIsAdmin(true);
      setAccessDenied(false);
    });
    return () => unsubscribe();
  }, []);

  const authenticated = authChecked && user && isAdmin;

  return (
    <div className="admin-page page-dark">
      <div className="admin-bg-glow admin-bg-glow-1" />
      <div className="admin-bg-glow admin-bg-glow-2" />
      <div className="admin-bg-overlay" />

      {/* Always render the admin shell (blurred when not authenticated) */}
      <div className={`admin-shell-wrap${authenticated ? '' : ' admin-shell-locked'}`}>
        <AdminShell user={user} authenticated={authenticated} />
      </div>

      {/* Login / loading overlay when not authenticated */}
      {!authenticated && (
        <div className="admin-login-overlay">
          <div className="admin-login-overlay-backdrop" />
          <div className="admin-login-overlay-content">
            {!authChecked ? (
              <div className="admin-login-modal">
                <p className="text-secondary" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                  Checking access…
                </p>
              </div>
            ) : accessDenied ? (
              <div className="admin-login-modal">
                <h2 className="admin-login-modal-title">Access Denied</h2>
                <p className="text-secondary" style={{ marginBottom: 'var(--space-4)' }}>
                  Only authorized administrators can use this area.
                </p>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setAccessDenied(false)}
                  style={{ width: '100%' }}
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <AdminLogin onSuccess={() => {}} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Renders the top nav + main content area. Always mounted so it shows blurred behind the login modal. */
function AdminShell({ user, authenticated }) {
  const location = useLocation();
  const pathname = location.pathname ?? '';

  const isAddProgram = pathname === '/admin/programs/new';
  const editMatch = pathname.match(/^\/admin\/programs\/([^/]+)\/edit$/);
  const isEditProgram = Boolean(editMatch);
  const editId = editMatch ? decodeURIComponent(editMatch[1]) : null;
  const isProgramsList = pathname === '/admin/programs' || pathname === '/admin';
  const isContent =
    pathname === '/admin/content' ||
    pathname.startsWith('/admin/content/');
  const isSettings = pathname === '/admin/settings';
  const isClubs = pathname === '/admin/clubs';
  const isDocs = pathname === '/admin/docs';

  let mainContent = null;
  let contentFullWidth = false;

  if (isAddProgram) {
    mainContent = <AdminProgramForm programId={editId} isNew={true} />;
    contentFullWidth = true;
  } else if (isEditProgram) {
    mainContent = (
      <>
        <AdminDashboard />
        <AdminProgramForm programId={editId} isNew={false} isFullScreen />
      </>
    );
    contentFullWidth = true;
  } else if (isProgramsList) {
    mainContent = <AdminDashboard />;
    contentFullWidth = true;
  } else if (isContent) {
    mainContent = <AdminContent />;
    contentFullWidth = true;
  } else if (isClubs) {
    mainContent = <AdminClubs />;
    contentFullWidth = true;
  } else if (isDocs) {
    mainContent = <AdminDocs />;
    contentFullWidth = true;
  } else if (isSettings) {
    mainContent = <AdminSettings />;
    contentFullWidth = true;
  }

  const mainClass = [
    'admin-main',
    mainContent && !contentFullWidth ? 'admin-main--form' : '',
    contentFullWidth ? 'admin-main--content' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <AdminNav authenticated={authenticated} />
      <main className={mainClass}>
        {mainContent ? (
          contentFullWidth ? mainContent : <div className="admin-main-centered">{mainContent}</div>
        ) : null}
      </main>
    </>
  );
}

export default AdminLayout;
